/**
 * Reset-password page (spec 0009 §5.6, §5.8).
 *
 * The emailed link carries the token in the URL *fragment*
 * (`/reset-password#token=prt_….<secret>`), which browsers never send
 * to a server. We read it once, keep it in component state only, and
 * immediately strip it from the address bar and history. It reaches
 * the API solely in the JSON body of POST /auth/password/reset.
 *
 * A successful reset mints no session: the operator signs in again,
 * including their authenticator code.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { KeyRound } from "lucide-react";
import axios from "axios";
import { asApiError, postResetPassword } from "@/lib/api";
import { readTokenFromHash } from "@/lib/reset-link";
import { useAuth } from "@/lib/use-auth";
import { AuthShell } from "@/components/auth/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const MIN_LENGTH = 12;

type FormError = { message: string; requestNewLink?: boolean };

function errorFor(err: unknown): FormError {
  const status = axios.isAxiosError(err) ? err.response?.status : undefined;
  const body = asApiError(err);
  if (body?.error === "invalid_or_expired_token") {
    return {
      message: "This reset link is invalid, already used, or expired.",
      requestNewLink: true,
    };
  }
  if (body?.error === "validation_error" && body.detail === "same_as_current") {
    return { message: "Choose a password that's different from your current one." };
  }
  if (body?.error === "validation_error") {
    return { message: `Use at least ${MIN_LENGTH} characters.` };
  }
  if (status === 429) {
    return { message: "Too many attempts from this network. Wait a while and try again." };
  }
  if (status === 503) {
    return {
      message: "Couldn't complete the reset right now. Nothing was changed, so try again in a moment.",
    };
  }
  if (status === 404) {
    return { message: "Self-service reset isn't enabled. Ask a super admin to rotate your password." };
  }
  return { message: "Couldn't reset your password. Check your network and try again." };
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  // Lazy initialiser: runs during render, before the effect below
  // strips the fragment — so StrictMode's double render still sees it.
  const [token] = useState<string | null>(() =>
    readTokenFromHash(window.location.hash),
  );
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<FormError | null>(null);

  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(
        window.history.state,
        "",
        window.location.pathname + window.location.search,
      );
    }
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_LENGTH) {
      setError({ message: `Use at least ${MIN_LENGTH} characters.` });
      return;
    }
    if (password !== confirm) {
      setError({ message: "The two passwords don't match." });
      return;
    }
    if (!token) return;
    setSubmitting(true);
    try {
      await postResetPassword({ token, new_password: password });
      // The reset revoked every session, but the cached principal doesn't
      // know yet. Without this, a still-signed-in operator is bounced from
      // /login to the dashboard and never sees the banner.
      await refresh();
      navigate("/login?reset=1", { replace: true });
    } catch (err) {
      setError(errorFor(err));
    } finally {
      setSubmitting(false);
    }
  }

  const requestLink = (
    <Link to="/forgot-password" className="text-foreground underline">
      Request a new link
    </Link>
  );

  if (!token) {
    return (
      <AuthShell
        title="Reset link incomplete"
        description="This page needs the full link from your reset email."
        footer={requestLink}
      >
        <p className="text-sm text-muted-foreground">
          Open the link from the email directly, or request a new one.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Choose a new password"
      description={`At least ${MIN_LENGTH} characters. Your authenticator stays the same, and all your sessions will be signed out.`}
      footer={
        <Link to="/login" className="text-foreground underline">
          Back to sign in
        </Link>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <div className="space-y-2">
          <Label htmlFor="new">New password</Label>
          <Input
            id="new"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_LENGTH}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm">Confirm new password</Label>
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_LENGTH}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={submitting}
          />
        </div>
        {error ? (
          <div
            role="alert"
            className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
          >
            <p>{error.message}</p>
            {error.requestNewLink ? <p>{requestLink}</p> : null}
          </div>
        ) : null}
        <Button type="submit" className="w-full" disabled={submitting} size="lg">
          <KeyRound />
          {submitting ? "Saving…" : "Set new password"}
        </Button>
      </form>
    </AuthShell>
  );
}

export default ResetPasswordPage;
